import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect, useMemo } from 'react';
import { getImageUrl } from '@/utils/images';

const LG_SIZE = 1024;
const MD_SIZE = 768;

type CarouselItem = {
    image?: {
        url: string;
        alt: string;
        width: number;
        height: number;
    };
    link?: {
        url: string;
        rel?: string;
        title: string;
        target?: string;
    };
    title?: string;
    description?: string;
};

export type CarouselProps = {
    slides: CarouselItem[];
    darkMode?: boolean;
    cropImage?: boolean;
    headingLevel?: string;
    numSlidesTablet?: number;
    numSlidesDesktop?: number;
    useZoomAnimation?: boolean;
    alwaysShowDescription?: boolean;
};

export const Carousel = ({
    slides = [],
    darkMode = false,
    cropImage = true,
    headingLevel = '3',
    numSlidesTablet = 3,
    numSlidesDesktop = 4,
    useZoomAnimation = false,
    alwaysShowDescription = false,
}: CarouselProps) => {
    const desktopRef = useRef<HTMLDivElement | null>(null);
    const defaultRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const slidesRef = useRef<(HTMLElement | null)[]>([]);

    const [index, setIndex] = useState(0);
    const [active, setActive] = useState(-1);
    const [canGoLeft, setCanGoLeft] = useState(false);
    const [canGoRight, setCanGoRight] = useState(false);

    const numSlides = useMemo(() => slides.length, [slides]);
    const showItems = useMemo(() => Math.min(numSlidesDesktop, numSlides), [numSlidesDesktop, numSlides]);
    const showItemsTablet = useMemo(() => Math.min(numSlidesTablet, numSlides), [numSlidesTablet, numSlides]);
    const showButtons = useMemo(() => numSlides > showItems, [numSlides, showItems]);

    const bigSize = useMemo(() => {
        if (numSlides === 4) {
            return 40;
        }
        if (showItems === 3) {
            return 50;
        }

        return 30;
    }, [numSlides, showItems]);

    const delta = useMemo(() => numSlides - 1, [numSlides]);
    const inactiveSize = useMemo(() => 100 / numSlides, [numSlides]);
    const totalOutside = useMemo(() => (numSlides - showItems) * inactiveSize, [numSlides, showItems]);
    const smallSize = useMemo(() => (100 - totalOutside - bigSize) / delta, [numSlides, showItems]);
    const totalSize = useMemo(() => (100 / showItems) * numSlides, [numSlides, showItems]);

    const getItemHeightClasses = (item: CarouselItem) => {
        const classes: string[] = [];
        const hasLink = !!item?.link?.url && !!item.link?.title;
        const descriptionLength = item?.description?.length || 0;

        if (!descriptionLength || (!useZoomAnimation && alwaysShowDescription)) {
            return classes;
        }

        if (numSlidesDesktop === 4) {
            if (descriptionLength > 250) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[660px]'
                            : 'lg:r-min-h-[620px]'
                        : hasLink
                          ? 'lg:r-min-h-[550px]'
                          : 'lg:r-min-h-[520px]'
                );
            } else if (descriptionLength > 200) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[610px]'
                            : 'lg:r-min-h-[580px]'
                        : hasLink
                          ? 'lg:r-min-h-[540px]'
                          : 'lg:r-min-h-[500px]'
                );
            } else if (descriptionLength > 100) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[590px]'
                            : 'lg:r-min-h-[560px]'
                        : hasLink
                          ? 'lg:r-min-h-[510px]'
                          : 'lg:r-min-h-[490px]'
                );
            } else {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[490px]'
                            : 'lg:r-min-h-[460px]'
                        : hasLink
                          ? 'lg:r-min-h-[495px]'
                          : 'lg:r-min-h-[470px]'
                );
            }
        } else {
            if (descriptionLength > 250) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[630px]'
                            : 'lg:r-min-h-[590px]'
                        : hasLink
                          ? 'lg:r-min-h-[530px]'
                          : 'lg:r-min-h-[500px]'
                );
            } else if (descriptionLength > 200) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[585px]'
                            : 'lg:r-min-h-[555px]'
                        : hasLink
                          ? 'lg:r-min-h-[520px]'
                          : 'lg:r-min-h-[480px]'
                );
            } else if (descriptionLength > 100) {
                classes.push(
                    alwaysShowDescription
                        ? hasLink
                            ? 'lg:r-min-h-[560px]'
                            : 'lg:r-min-h-[535px]'
                        : hasLink
                          ? 'lg:r-min-h-[510px]'
                          : 'lg:r-min-h-[480px]'
                );
            } else {
                classes.push(
                    alwaysShowDescription ? (hasLink ? 'r-min-h-[510px]' : 'r-min-h-[480px]') : hasLink ? 'r-min-h-[490px]' : 'r-min-h-[450px]'
                );
            }
        }

        return classes;
    };

    function getItemClasses(largeWithZoom = false, item: CarouselItem) {
        const classes = getItemHeightClasses(item);

        if (numSlidesTablet > 2 && numSlides > 2) {
            classes.push('md:r-min-w-[calc(33%-0.75rem)]');
        } else {
            classes.push('md:r-min-w-[calc(50%-0.75rem)]');
        }

        if (largeWithZoom) {
            return classes;
        }

        if (numSlidesDesktop > 3 && numSlides > 3) {
            classes.push('xl:r-min-w-[calc(25%-1.25rem)]');
        } else {
            classes.push('xl:r-min-w-[calc(33%-0.75rem)]');
        }

        return classes;
    }

    function getHeaderTag() {
        switch (headingLevel) {
            case '2':
                return 'h2';
            case '3':
                return 'h3';
            case '4':
                return 'h4';
            case '5':
                return 'h5';
            case '6':
                return 'h6';
        }

        return 'h3';
    }

    const onMouseOver = (index: number) => {
        if (index !== active) {
            setActive(index);
        }

        const slide = slidesRef.current?.[index] ?? null;
        if (!slide) {
            return;
        }

        slide.classList.add('hovering');

        setTimeout(() => {
            if (slides[index].description?.length) {
                slide.classList.add('finished-animating');
            }
        }, 230);
    };

    const onMouseOut = (index: number) => {
        if (index === active) {
            setActive(-1);
        }

        const slide = slidesRef.current?.[index] ?? null;
        if (!slide) {
            return;
        }

        slide.classList.remove('hovering');
        if (slides[index].description?.length) {
            slide.classList.remove('finished-animating');
        }
    };

    const getItems = (largeWithZoom = false) =>
        slides.map((item, itemIndex) => {
            const inViewport = itemIndex >= index && itemIndex < index + showItems;
            const style: React.CSSProperties = {};
            const buffer = 24;

            if (largeWithZoom) {
                style.minWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                style.transition = 'all ease-in-out 230ms';
                if (!inViewport) {
                    style.minWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                    style.maxWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                    delete style.transition;
                } else {
                    if (itemIndex === active && useZoomAnimation) {
                        style.minWidth = `calc(${bigSize}% - ${buffer}px)`;
                    } else if (active !== -1) {
                        style.minWidth = `calc(${smallSize}% - ${buffer}px)`;
                    }
                }
            }

            const HeaderTag = getHeaderTag();

            const props: { to: string; [key: string]: unknown } = {
                style,
                to: '',
                onMouseOut: () => onMouseOut(itemIndex),
                onMouseOver: () => onMouseOver(itemIndex),
                className: clsx(
                    'no-icon carousel-item r-group r-flex r-flex-1 r-snap-start r-flex-col r-overflow-hidden r-duration-300 max-md:r-min-w-[80vw] max-md:r-pl-4 lg:r-w-full',
                    getItemClasses(largeWithZoom, item)
                ),
            };

            if (item.link?.url) {
                props.target = item.link?.target || '_self';
                props.to = item.link?.url || '/';
                props.rel = item.link?.rel || '';
            }

            const WrapperTag = item.link?.url ? Link : 'div';

            return (
                <WrapperTag
                    {...props}
                    key={itemIndex}
                    ref={(el: HTMLElement | null) => {
                        if (largeWithZoom) {
                            slidesRef.current[itemIndex] = el;
                        }
                    }}
                >
                    <div
                        role="img"
                        aria-label={item.image?.alt || ''}
                        style={{ backgroundImage: `url(${getImageUrl(item.image?.url)})` }}
                        className={clsx(
                            'r-max-h-[350px] r-min-h-[350px] r-w-full r-flex-1 r-bg-center r-bg-no-repeat r-p-2',
                            cropImage ? 'r-rounded-xl r-bg-cover' : 'r-bg-contain'
                        )}
                    />
                    <div className="r-pt-4">
                        <HeaderTag className="r-pb-1 r-text-lg r-font-bold">{item.title}</HeaderTag>
                        <div
                            className={clsx({
                                'r-overflow-hidden r-transition-all r-duration-200 lg:r-max-h-0 lg:r-opacity-0':
                                    useZoomAnimation && !alwaysShowDescription,
                                'carousel-description-animated': active === itemIndex,
                            })}
                        >
                            <p className="r-text-sm" dangerouslySetInnerHTML={{ __html: item.description || '' }} />
                        </div>
                        {item.link?.url && item.link?.title && (
                            <p className="r-flex r-items-center r-gap-2 r-pt-2 r-text-base">
                                {item.link?.title}{' '}
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="17" viewBox="0 0 14 17" fill="none">
                                    <mask id="mask0_195_5467" maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="17">
                                        <path d="M14 0.5H0V16.5H14V0.5Z" fill="white" />
                                    </mask>
                                    <g mask="url(#mask0_195_5467)">
                                        <path
                                            d="M13.7062 9.207L8.70618 14.207C8.51245 14.4039 8.25618 14.5008 7.99998 14.5008C7.74372 14.5008 7.48805 14.4031 7.29305 14.2078C6.90245 13.8172 6.90245 13.1844 7.29305 12.7937L10.5875 9.5008H0.999949C0.447762 9.5008 0 9.05393 0 8.5008C0 7.94766 0.447762 7.5008 0.999949 7.5008H10.5875L7.29372 4.20703C6.90305 3.8164 6.90305 3.18359 7.29372 2.79296C7.68432 2.40235 8.31712 2.40235 8.70778 2.79296L13.7078 7.793C14.0968 8.18513 14.0968 8.8164 13.7062 9.207Z"
                                            fill={darkMode ? '#fff' : '#141619'}
                                        />
                                    </g>
                                </svg>
                            </p>
                        )}
                    </div>
                </WrapperTag>
            );
        });

    function getCurrentRef() {
        if (useZoomAnimation) {
            if (!desktopRef?.current || !containerRef?.current || !defaultRef?.current) {
                return null;
            }

            return containerRef!.current!.offsetWidth >= LG_SIZE ? desktopRef!.current! : defaultRef!.current!;
        }

        return defaultRef!.current || null;
    }

    function getScrollAmount(ref: HTMLElement) {
        if (ref.clientWidth < MD_SIZE) {
            return ref.clientWidth;
        }

        const numItems = ref.clientWidth >= LG_SIZE ? showItems : showItemsTablet;
        return ref.clientWidth / numItems;
    }

    const slide = (direction: number) => {
        if ((direction > 0 && !canGoRight) || (direction < 0 && !canGoLeft)) {
            return;
        }

        const ref = getCurrentRef();
        if (!ref) {
            return;
        }

        const scrollAmount = getScrollAmount(ref);
        ref.scrollBy({ left: direction * scrollAmount + 16 * direction + 1 * direction, behavior: 'smooth' });
    };

    const handleScroll = () => {
        const ref = getCurrentRef();
        if (!ref) {
            return;
        }

        const left = ref.scrollLeft > 0;
        const right = ref.scrollLeft < ref.scrollWidth - containerRef!.current!.offsetWidth - 8;
        const itemWidth = containerRef!.current!.offsetWidth / showItems;

        setCanGoLeft(left);
        setCanGoRight(right);

        // set the correct activet item so we can calculate later whats offscreen
        // We do do this on scroll so that even when someone scrolls, we know where we are
        setIndex(Math.floor(Math.ceil(ref.scrollLeft) / Math.ceil(itemWidth)));
    };

    useEffect(() => {
        desktopRef.current?.addEventListener('scroll', handleScroll);
        defaultRef.current?.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            desktopRef.current?.removeEventListener('scroll', handleScroll);
            defaultRef.current?.removeEventListener('scroll', handleScroll);
        };
    }, [desktopRef, containerRef]);

    const leftButtonClasses = clsx(
        'max-md:r-hidden r-absolute r-left-4 lg:-r-left-6 r-top-1/2 r--translate-y-[80px] r-rounded-full r-bg-white r-shadow r-z-10 r-p-2 r-translate-x-7 lg:r-translate-x-10 r-transition-opacity',
        {
            'r-opacity-0': !canGoLeft,
        }
    );

    const rightButtonClasses = clsx(
        'max-md:r-hidden r-absolute r-right-4 lg:-r-right-6 r-top-1/2 r--translate-y-[80px] r-rounded-full r-bg-white r-shadow r-z-10 r-p-2 r--translate-x-7 lg:r--translate-x-10 r-transition-opacity',
        {
            'r-opacity-0': !canGoRight,
        }
    );

    return (
        <div
            ref={containerRef}
            className={clsx('r-flex max-lg:r-pb-8', {
                'md:r-overflow-hidden': !useZoomAnimation,
                'r-text-white': darkMode,
            })}
        >
            {showButtons && (
                <>
                    <button onClick={() => slide(-1)} className={leftButtonClasses}>
                        <svg className="r-h-6 r-w-6 r-pr-[2px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M15.7071 4.29289C16.0976 4.68342 16.0976 5.31658 15.7071 5.70711L9.41421 12L15.7071 18.2929C16.0976 18.6834 16.0976 19.3166 15.7071 19.7071C15.3166 20.0976 14.6834 20.0976 14.2929 19.7071L7.29289 12.7071C7.10536 12.5196 7 12.2652 7 12C7 11.7348 7.10536 11.4804 7.29289 11.2929L14.2929 4.29289C14.6834 3.90237 15.3166 3.90237 15.7071 4.29289Z"
                                fill="#000000"
                            />
                        </svg>
                    </button>

                    <button onClick={() => slide(1)} className={rightButtonClasses}>
                        <svg className="r-h-6 r-w-6 r-pl-[2px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.29289 4.29289C8.68342 3.90237 9.31658 3.90237 9.70711 4.29289L16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L9.70711 19.7071C9.31658 20.0976 8.68342 20.0976 8.29289 19.7071C7.90237 19.3166 7.90237 18.6834 8.29289 18.2929L14.5858 12L8.29289 5.70711C7.90237 5.31658 7.90237 4.68342 8.29289 4.29289Z"
                                fill="#000000"
                            />
                        </svg>
                    </button>
                </>
            )}

            <div
                ref={defaultRef}
                className={clsx(
                    'scrollbar-hide r-flex r-w-full r-snap-x r-snap-mandatory r-overflow-x-auto r-transition-all r-duration-300 max-md:-r-mx-4 max-md:r-min-w-[100vw] max-md:r-max-w-[100vw] max-md:r-pr-6 max-md:last:r-pr-4 md:r-gap-6',
                    {
                        'lg:r-hidden': useZoomAnimation,
                    }
                )}
            >
                {getItems()}
            </div>

            {useZoomAnimation && (
                <div className="scrollbar-hide r-w-full r-snap-x r-snap-mandatory r-overflow-x-scroll max-lg:r-hidden" ref={desktopRef}>
                    <div
                        style={{ width: `calc(${totalSize}%)` }}
                        className="r-hidden r-snap-x r-snap-start r-gap-6 r-scroll-smooth r-transition-all r-duration-300 lg:r-flex"
                    >
                        {getItems(true)}
                    </div>
                </div>
            )}
        </div>
    );
};
